import {
    InvoiceAction,
    InvoiceMutationDto,
    InvoiceStatus,
    InvoiceTemplateDto,
    LineItemGroupMutationDto,
    LineItemMutationDto,
    MetaDataDto,
} from '../../../config/finance/invoiceConfig'
import useEntity from '../../../hooks/useEntity'
import { mapInvoiceTemplate, mapLineItemGroupTemplate } from '../../../components/Form/Finance/Invoice/InvoiceFile'
import { IFileDto } from '../../../config/fileConfig'
import { DataPromise } from '../../../context/AxiosContext'
import { MainDto } from '../../../config/filePartyConfig'
import useLineItemEntities from './useLineItemEntities'
import { PersistInvoiceDto } from './usePersistInvoice'
import { useDispatch } from 'react-redux'
import useUnits from '../../useUnits'
import useAxios from '../../useAxios'

type Dto = Pick<
    InvoiceMutationDto,
    'templateId' | 'clientPivotId' | 'debtorPivotId' | 'currency' | 'recipientReference'
>

type GroupPrices = {
    [groupTemplateId: string]: string
}

export type PrefillLineItems = {
    [groupTemplateId: string]: LineItemMutationDto[]
}

export type PersistLegalAidCertificateInvoice = (
    requestId: string,
    dto: Dto,
    callback: (data: PersistInvoiceDto) => void,
    prices: GroupPrices,
    prefillLineItems?: PrefillLineItems,
) => void

export default function usePersistLegalAidCertificateInvoice(
    file: DataPromise<IFileDto>,
    parties: DataPromise<MainDto[]>,
): PersistLegalAidCertificateInvoice {
    const dispatch = useDispatch()
    const axios = useAxios()

    const [templates] = useEntity<InvoiceTemplateDto[]>(() => ({ url: '/finance/invoice/template' }), false)
    const [meta] = useEntity<MetaDataDto>(
        fileId => ({ url: `/finance/invoice/meta/file/${fileId}` }),
        file.data?.id ?? null,
    )
    const entities = useLineItemEntities(meta)
    const { activeUnits } = useUnits()

    return (requestId, dto, callback, prices, prefillLineItems) => {
        if (!templates.loaded || !file.loaded || !parties.loaded || !meta.loaded) {
            return undefined
        }
        const template: InvoiceTemplateDto = templates.data.find(template => template.id === dto.templateId)
        const debtor: MainDto = parties.data.find(party => party.pivotId === dto.debtorPivotId)

        if (!template || !debtor) {
            return undefined
        }

        const invoice: InvoiceMutationDto = mapInvoiceTemplate(template, {
            ...dto,
            status: InvoiceStatus.Final,
            fileId: file.data.id,
            name: file.data.name,
            unit: file.data.unit || (activeUnits?.length === 1 && activeUnits[0].key),
            accounting: file.data.accounting,
            recipientEmail: debtor.email,
            recipientReference: dto.recipientReference || debtor.reference,
            language: file.data.language,
            messageSubject:
                (meta.data.emailMessages || [])
                    .filter(message => message.language === file.data.language)
                    .find(message => !message.currency || message.currency === file.data.currency)?.subject || '',
            messageBody:
                (meta.data.emailMessages || [])
                    .filter(message => message.language === file.data.language)
                    .find(message => !message.currency || message.currency === file.data.currency)
                    ?.body?.replaceAll('<br/>', '\n') || '',
            paymentMessageBody:
                file.data.paymentCurrency &&
                (meta.data?.paymentMessages || [])
                    .filter(message => message.language === file.data.language)
                    .find(message => !message.currency || message.currency === file.data.paymentCurrency)
                    ?.body?.replaceAll('<br/>', '\n'),
        })

        invoice.lineItemGroups = template.lineItemGroups
            .map<LineItemGroupMutationDto>(template => {
                const group: LineItemGroupMutationDto = mapLineItemGroupTemplate(template, entities)

                if (!group) {
                    return undefined
                }

                return {
                    ...group,
                    price: prices[Object.keys(prices).find(templateId => templateId === template.id)],
                    lineItems: [
                        ...(group?.lineItems || []),
                        ...(prefillLineItems?.[
                            Object.keys(prefillLineItems).find(templateId => templateId === template.id)
                        ] || []),
                    ].filter(lineItem => lineItem.entityId),
                }
            })
            .filter(Boolean)
        dispatch({ type: InvoiceAction.ResetOverview })

        axios
            .post('/finance/invoice', invoice)
            .then(result => result.data)
            .then((result: PersistInvoiceDto) => callback(result))
    }
}
